import React, { useContext, useEffect, useState } from 'react'
import postService from '../../../services/postService'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import PostCard from '../HomePage/PostCard/PostCard'
import PostLoading from '../../commonUIComponents/PostLoading'
import { Box, Button, Divider, Grid, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material'
import communityService from '../../../services/communityService'
import { useSnackbar } from 'notistack'
import { AuthTokenContext } from '../../contexts/AuthTokenContext'
import CommunityPageHeaderLoading from '../../commonUIComponents/CommunityHeaderLoading'
import UpdateCommunityPage from '../UpdateCommunityPage'
import { CommunityPageAvatar } from '../../commonUIComponents/CustomAvatar'
import AddIcon from '@mui/icons-material/Add'
import NotificationsIcon from '@mui/icons-material/Notifications'
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded'
import { UserInfoContext } from '../../contexts/UserInfoContext'
import CardHeader from '@mui/material/CardHeader'
import { SearchBarContext } from '../../contexts/SearchBarContext'

const CommunityPage = () => {
  const { authToken } = useContext(AuthTokenContext)
  const { userInfo } = useContext(UserInfoContext)
  const { setCommunityId } = useContext(SearchBarContext)
  const [searchParams] = useSearchParams()
  const [community, setCommunity] = useState()
  const [posts, setPosts] = useState()
  const { enqueueSnackbar } = useSnackbar()
  const [page, setPage] = useState(0)
  const [pageSize] = useState(10)
  const [isLastPage, setIsLastPage] = useState(false)
  const [postLoading, setPostLoading] = useState(true)
  const [communityHeaderLoading, setCommunityHeaderLoading] = useState(true)
  const { communityId } = useParams()
  const [anchorEl, setAnchorEl] = React.useState(null)
  const open = Boolean(anchorEl)
  const [openUpdateCommunityModal, setOpenUpdateCommunityModal] = useState(false)

  const fetchCommunity = () => {
    setCommunityHeaderLoading(true)
    communityService.getCommunityById(communityId)
      .then(res => {
        setCommunity(res)
      })
      .catch(err => enqueueSnackbar(err.response?.data.message, { variant: 'warning' }))
      .finally(() => setCommunityHeaderLoading(false))
  }

  const fetchPosts = () => {
    setPostLoading(true)
    postService.fetchPosts(page, pageSize, communityId, searchParams.get('keyword'))
      .then(res => {
        if (posts && page > 0) {
          setPosts([...posts, ...res.posts])
        } else {
          setPosts(res.posts)
        }
        setIsLastPage(res.pagination.isLastPage)
      })
      .catch(err => console.log(err))
      .finally(() => setPostLoading(false))
  }

  const updateVote = (postId, userVote, totalVotes) => {
    const currentPosts = [...posts]
    currentPosts.forEach(post => {
      if (post.id === postId) {
        post.myVote = userVote
        post.votes = totalVotes
      }
    })
    setPosts(currentPosts)
  }

  useEffect(() => {
    setCommunityId(communityId)
    fetchCommunity()
    setPage(0)
    fetchPosts()
  }, [communityId])

  useEffect(() => {
    fetchPosts()
  }, [page, searchParams])

  const paginationHandler = () => {
    setPage(page + 1)
  }

  const handleModalClose = () => {
    setOpenUpdateCommunityModal(false)
  }

  const updateCommunityHandler = () => {
    setAnchorEl(null)
    setOpenUpdateCommunityModal(true)
  }

  const openMenuHandler = (event) => {
    setAnchorEl(event.currentTarget)
  }
  const closeMenuHandler = () => {
    setAnchorEl(null)
  }

  const joinOnClickHandler = () => {
    communityService.joinCommunity(communityId)
      .then(res => {
        console.log('RETURNED VALUE: ', res)
      })
  }

  const CommunityBanner = () => {
    return (
      <Grid item xs={12}>
        <Box
          component="img"
          sx={{
            overflow: 'hidden',
            borderRadius: '10px',
            maxHeight: 150,
            width: 1,
            mt: '0.5rem'
          }}
          alt="Community Banner"
          src={community?.heroImageUrl}
        />
      </Grid>
    )
  }

  const CommunityUserActionButtonList = (
      <Box>
        <IconButton component={Link} to={`/create-post/${community?.id}`} sx={{ height: 30, borderRadius: 10, border: '1px solid #ddd' }}>
          <AddIcon />
          <Typography variant='subtitle2' sx={{ fontSize: 13, fontWeight: 'bold' }}>Create a post</Typography>
        </IconButton>
        <IconButton sx={{ ml: 1, height: 30, borderRadius: 10, border: '1px solid #ddd' }}>
          <NotificationsIcon />
        </IconButton>
        { community?.isUserJoined
          ? (
            <IconButton sx={{ ml: 1, height: 30, borderRadius: 10, border: '1px solid #ddd' }} onClick={joinOnClickHandler}>
              <Typography variant='subtitle2' sx={{ fontSize: 13, fontWeight: 'bold' }}>Joined</Typography>
            </IconButton>
            )
          : (
            <IconButton color="info" sx={{ ml: 1, height: 30, borderRadius: 10, border: '1px solid #ddd' }} onClick={joinOnClickHandler}>
              <Typography variant='subtitle2' sx={{ fontSize: 13, fontWeight: 'bold' }}>Join</Typography>
            </IconButton>
            )
        }
        <Tooltip title="Community settings">
          <IconButton
            onClick={openMenuHandler}
            aria-controls={open ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
          >
            <MoreHorizRoundedIcon sx={{ width: 30, height: 30, borderRadius: 25, border: '1px solid #ddd' }} />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={anchorEl}
          id="community-menu"
          open={open}
          onClose={openMenuHandler}
          onClick={closeMenuHandler}
          PaperProps={{
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1
              },
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0
              }
            }
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {userInfo?.role === 'ADMIN' &&
            (
              <MenuItem onClick={updateCommunityHandler}>
                Update community
              </MenuItem>
            )
          }
          <MenuItem onClick={closeMenuHandler}>
            Add to custom feed
          </MenuItem>
          <MenuItem onClick={closeMenuHandler}>
            Add to favorites
          </MenuItem>
          <MenuItem onClick={closeMenuHandler}>
            Mute r/{community?.id}
          </MenuItem>
        </Menu>
      </Box>
  )

  const CommunityHeader = (
      <Grid container spacing={2}>
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <CardHeader
            avatar={
              <CommunityPageAvatar src={community?.avatarImageUrl} communityId={community?.id} />
            }
            titleTypographyProps={{ variant: 'h5', marginLeft: '-0.5rem', fontSize: '30px', fontWeight: 'bold' }}
            title={<>r/{community?.id}</>}
          />
        </Box>
        <Box sx={{ display: { xs: 'none', sm: 'block', md: 'none' } }}>
          <CardHeader
            avatar={
              <CommunityPageAvatar src={community?.avatarImageUrl} communityId={community?.id} />
            }
            titleTypographyProps={{ variant: 'h5', marginLeft: '-0.5rem', fontSize: '30px', fontWeight: 'bold' }}
            title={<>r/{community?.id}</>}
          />
          <Box sx={{ mt: -2, ml: 4 }}>
            {CommunityUserActionButtonList}
          </Box>
        </Box>
        <Box sx={{ display: { xs: 'block', sm: 'none', pt: 1 } }}>
          <CardHeader
            avatar={
              <CommunityPageAvatar src={community?.avatarImageUrl} communityId={community?.id} ml={2} mt={0} width={40} height={40} />
            }
            titleTypographyProps={{ variant: 'body1', marginLeft: '-0.5rem', fontWeight: 'bold' }}
            title={<>r/{community?.id}</>}
          />
          <Box sx={{ mt: -2, ml: 4 }}>
            {CommunityUserActionButtonList}
          </Box>
        </Box>
      </Grid>
  )

  const CommunityPosts = (
      <Grid item xs>
        <Grid item xs={12}>
          {CommunityHeader}
        </Grid>
        <Grid item xs={12}>
          <Divider sx={{ borderBottomWidth: 1 }}/>
        </Grid>
        {!postLoading
          ? (
            <Grid item xs={12}>
              {posts?.map(post => <PostCard key={post.id} post={post} fetchPosts={fetchPosts} updateVote={updateVote} />)}
            </Grid>
            )
          : (
            <Grid item xs={12}>
              {posts?.map(post => <PostCard key={post.id} post={post} fetchPosts={fetchPosts} updateVote={updateVote} />)}
              <PostLoading loading={postLoading}/>
            </Grid>
            )
        }
        <Grid item xs={12}>
          {!isLastPage
            ? <Button onClick={paginationHandler}>Show More</Button>
            : undefined}
        </Grid>
      </Grid>
  )

  const CommunityInformation = (
      <Grid item xs="auto" sx={{ display: { xs: 'none', md: 'block' } }}>
        {authToken
          ? (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {CommunityUserActionButtonList}
              </Grid>
              <Grid item xs={12}>

              </Grid>
            </Grid>
            )
          : (
            <></>
            )
        }
      </Grid>
  )

  return (
    <>
      {!communityHeaderLoading
        ? (
          <>
            {community
              ? (
                <Grid container>
                  <CommunityBanner />
                  <Grid container>
                    {CommunityPosts}
                    {CommunityInformation}
                  </Grid>
                  {openUpdateCommunityModal && <UpdateCommunityPage community={community} open={openUpdateCommunityModal} onClose={handleModalClose} submitOnSuccess={fetchCommunity} />}
                </Grid>
                )
              : undefined
            }
          </>
          )
        : <CommunityPageHeaderLoading loading={communityHeaderLoading} />
      }
    </>
  )
}

export default CommunityPage
